







import { defineComponent, getCurrentInstance, computed } from '@vue/composition-api';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import Vue from 'vue';
import { notNil } from '@berlin-front/common/utils';

/**
 * ## ContextProviderRouterView
 *
 * routerレベルでvueコンポーネントに対応するContextProviderを差し込みます。
 * routerのcomponents設定に
 *
 * ```
 * {
 *   path: '/path',
 *   name: 'SomeComponent',
 *   // componentではなく、components形式で書く。
 *   components: {
 *     default: SomeComponent,
 *     // defaultコンポーネントがここに書いたcontextProviderにラップされる。
 *     contextProvider: SomeComponentContext.provider,
 *   },
 *   // 通常のvue-routerと同じように、propsも使用可能。
 *   props: {
 *     default: (route) => ....,
 *     contextProvider: (route) => ....
 *   }
 * },
 * ```
 *
 * というような感じでcontextProviderを定義することで、対象のコンポーネントの直上にコンテキストプロバイダをセットすることができる。
 * 基本的にはcontextProviderのvalue値はdefault値をセットする実装で良いが、props
 */
export default defineComponent({
  name: 'ContextProviderRouterView',
  setup() {
    const router = useRouter();
    // 現在描画中のContextProviderRouterView
    const currentInstance = notNil(getCurrentInstance()).proxy;

    // ルーターで仕込むContextProviderと、Providerに指定するvalue値を取り出す。
    const providerProps = computed(() => {
      // routingを構成するインスタンスを配列に積み重ねたもの。
      const routeInstancies = currentInstance.$route.matched.map((match) => match.instances.default).filter((v) => v);
      // do-whileしながら、ルーターviewが配置されてるrouteのインスタンスを探す。
      // つまり親ルートのインスタンスを探す。そのルート階層のindexを得る。
      let instance = currentInstance as unknown as Vue | null;
      let index = -1;
      do {
        index = instance ? routeInstancies.indexOf(instance) : -1;
        instance = instance?.$parent ?? null;
      } while (index === -1 && instance);
      // 現在のインスタンスが、積み重ねたインスタンスのどのインスタンスの子か。積み重ねのindexで表してるのがindex。
      // nextRouteIndexは+1して、そのroute構成instanceの次のroute構成コンポーネントのindex。
      const nextRouteIndex = index + 1;
      const nextRoute = router.currentRoute.matched[nextRouteIndex];
      const provider =
        nextRoute !== undefined && 'contextProvider' in nextRoute.components
          ? nextRoute.components.contextProvider
          : null;
      // 一応contextProviderにdefault以外のvalue値を与えられるようにしておく。多分使用することはないとおもうが、vue-routerの仕様に準じる、
      const props = (() => {
        if (
          provider &&
          nextRoute !== undefined &&
          typeof nextRoute.props === 'object' &&
          'contextProvider' in nextRoute.props
        ) {
          const { $route } = currentInstance;
          if (typeof nextRoute.props.contextProvider === 'boolean') {
            return nextRoute.props.contextProvider ? $route.params : undefined;
          }
          if (typeof nextRoute.props.contextProvider === 'function') {
            return nextRoute.props.contextProvider($route);
          }
          return nextRoute.props.contextProvider;
        }
        return undefined;
      })();

      return provider ? { props, provider } : undefined;
    });
    return {
      providerProps,
    };
  },
});
